/**
 * Submitting.
 *
 * The outbox and blob store are owned elsewhere (`src/core/`). This module takes them as
 * arguments rather than importing them, for two reasons: it keeps the form engine testable
 * without a fake IndexedDB, and it makes the dependency on someone else's contract explicit at
 * one call site instead of diffuse across the renderer. `submit-core.ts` is the one-line wiring
 * that binds the real implementations.
 *
 * Submitting is `enqueue('submission', payload)` and nothing else. There is no online path and
 * no fallback: the queue is the only way out, so the offline case is not a special case.
 */

import { validate } from './validate.ts';
import { buildSubmission } from './payload.ts';
import type { BuildOptions, SubmissionPayload } from './payload.ts';
import type { Problem } from './validate.ts';
import type { FormSchema, FormState, PhotoRef } from './types.ts';

/** The two functions this module needs from `src/core/`. Signatures are fixed by that module. */
export interface SubmitDeps {
  /** `enqueue` from `src/core/outbox`. Returns the queued item. */
  readonly enqueue: <T>(kind: string, payload: T) => Promise<{ id: string; idempotencyKey: string }>;
}

/** The blob store, for handing captured bytes over before they become a `PhotoRef`. */
export interface BlobDeps {
  /** `putBlob` from `src/core/blobs`. Returns the blob id. */
  readonly putBlob: (blob: Blob) => Promise<string>;
}

export const SUBMISSION_KIND = 'submission';

export type SubmitResult =
  | { readonly ok: true; readonly outboxId: string; readonly idempotencyKey: string; readonly payload: SubmissionPayload }
  | { readonly ok: false; readonly problems: readonly Problem[] };

/**
 * Validates, builds and enqueues.
 *
 * Validation runs against the same visibility computation the payload build uses, so it is
 * impossible for validation to demand a field the payload would then omit.
 */
export async function submitForm(
  schema: FormSchema,
  state: FormState,
  deps: SubmitDeps,
  options: BuildOptions,
): Promise<SubmitResult> {
  const opts: Parameters<typeof validate>[2] = options.visibility ? { visibility: options.visibility } : {};
  const problems = validate(schema, state, opts);
  if (problems.length > 0) return { ok: false, problems };

  const payload = buildSubmission(schema, state, options);
  const item = await deps.enqueue(SUBMISSION_KIND, payload);
  return { ok: true, outboxId: item.id, idempotencyKey: item.idempotencyKey, payload };
}

/**
 * Stores captured bytes and returns the reference a photo field holds.
 *
 * Only needed by a capture component that hands over raw bytes. `src/capture/photoPipeline.ts`
 * already writes through its own `BlobSink` and returns a `PhotoAttachment`, which is assignable to
 * `PhotoRef` directly — in that case this function is not on the path at all.
 */
export async function storePhoto(blob: Blob, deps: BlobDeps, at: number, label?: string): Promise<PhotoRef> {
  const blobId = await deps.putBlob(blob);
  return { blobId, at, ...(label !== undefined ? { label } : {}) };
}
